import {inject, Injectable} from '@angular/core';
import {collection, doc} from 'firebase/firestore';
import {DataSnapshot, equalTo, get, orderByChild, query, ref, remove} from 'firebase/database';
import {deleteDoc, getDocs} from 'firebase/firestore';
import {deleteObject, listAll, ref as storeRef} from 'firebase/storage';
import {FirebaseInitService} from '../shared/firebase-init.service';

@Injectable()
export class ProfileCleanupService {
	private readonly firebaseInit = inject(FirebaseInitService);

	private get storage() {
		return this.firebaseInit.storage;
	}

	private get database() {
		return this.firebaseInit.database;
	}

	private get firestore() {
		return this.firebaseInit.firestore;
	}

	clearUserData(uid: string) {
		const storeRefe = this.buildStorageRef(uid);
		const invitRef = this.buildInvitationCollectionRef();
		const roomRef = this.buildRoomRef();
		const statusRef = this.buildStatusRef();
		const notifRef = this.buildNotificationRef(uid);

		return this.clearAllUserData(storeRefe, invitRef, roomRef, statusRef, notifRef, uid);
	}

	async clearAllUserData(
		storeRefe: ReturnType<typeof storeRef>,
		invitRef: ReturnType<typeof collection>,
		roomRef: ReturnType<typeof ref>,
		statusRef: ReturnType<typeof ref>,
		notifRef: ReturnType<typeof ref>,
		uid: string
	) {
		const messageWaiters = this.deleteRoomData(uid, roomRef);
		const storeItems = await this.listAllItems(storeRefe);
		const storeAllWaiter = await Promise.allSettled(storeItems.map((itemRef) => this.deleteStorageItem(itemRef)));
		const inviteWaiter = this.deleteUserInvites(uid, invitRef, 'invitation');
		const notifWaiter = await this.removeRef(notifRef);
		const statusWaiter = await this.removeRef(statusRef);

		return (await Promise.allSettled([storeAllWaiter, inviteWaiter, notifWaiter, messageWaiters, statusWaiter]));
	}

	async deleteUserInvites(uid: string, instanceRef: ReturnType<typeof collection>, dbPath: string): Promise<PromiseSettledResult<void>[]> {
		const invitationWaiters: Promise<void>[] = [];
		const docs = await this.getDocsSnapshot(instanceRef);
		docs.forEach((sdoc: {data: () => Record<string, unknown>; id: string}) => {
			if (sdoc.data()['sender'] === uid || sdoc.data()['receiver'] === uid) {
				invitationWaiters.push(this.deleteDocByPath(`${dbPath}/${sdoc.id}`));
			}
		});
		return Promise.allSettled(invitationWaiters);
	}

	async deleteRoomData(uid: string, roomRef: ReturnType<typeof ref>): Promise<PromiseSettledResult<void>[]> {
		const messageWaiters: Promise<void>[] = [];
		const snapshot = await this.getQuerySnapshot(query(roomRef, orderByChild('isPrivate'), equalTo(true, 'isPrivate')));
		snapshot.forEach((childSnapshot: DataSnapshot) => {
			const roomData = childSnapshot.val() as {member?: string[]};
			if (roomData?.member?.some((m: string) => m === uid) && childSnapshot.key) {
				messageWaiters.push(this.deleteDocByPath(`roomMessages/${childSnapshot.key}`));
				messageWaiters.push(this.removeRef(ref(this.database, `rooms/${childSnapshot.key}`)));
				messageWaiters.push(this.removeRef(ref(this.database, `rooms_last_message/${childSnapshot.key}`)));
			}
		});
		return Promise.allSettled(messageWaiters);
	}

	protected buildStorageRef(uid: string) {
		return storeRef(this.storage, `users-gallery/${uid}`);
	}

	protected buildInvitationCollectionRef() {
		return collection(this.firestore, 'invitation');
	}

	protected buildRoomRef() {
		return ref(this.database, 'rooms');
	}

	protected buildStatusRef() {
		return ref(this.database, 'status');
	}

	protected buildNotificationRef(uid: string) {
		return ref(this.database, `notification/${uid}`);
	}

	protected listAllItems(storageRef: ReturnType<typeof storeRef>) {
		return listAll(storageRef).then((result) => result.items);
	}

	protected deleteStorageItem(itemRef: ReturnType<typeof storeRef>) {
		return deleteObject(itemRef);
	}

	protected removeRef(instanceRef: ReturnType<typeof ref>) {
		return remove(instanceRef);
	}

	protected getDocsSnapshot(instanceRef: ReturnType<typeof collection>) {
		return getDocs(instanceRef);
	}

	protected deleteDocByPath(path: string) {
		return deleteDoc(doc(this.firestore, path));
	}

	protected getQuerySnapshot(queryInstance: ReturnType<typeof query>) {
		return get(queryInstance);
	}
}
